import type { PartialBlock } from '@blocknote/core';
import { SideMenuExtension } from '@blocknote/core/extensions';
import {
  blockTypeSelectItems,
  useBlockNoteEditor,
  useComponentsContext,
  useDictionary,
  useExtensionState,
  type BlockTypeSelectItem,
} from '@blocknote/react';
import { useMemo, type ReactNode } from 'react';

type TurnIntoBlockItemProps = {
  children: ReactNode;
};

export function TurnIntoBlockItem({ children }: TurnIntoBlockItemProps) {
  const Components = useComponentsContext()!;
  const dict = useDictionary();
  const editor = useBlockNoteEditor();
  const block = useExtensionState(SideMenuExtension, {
    editor,
    selector: (state) => state?.block,
  });

  const items = useMemo(() => {
    return blockTypeSelectItems(dict).filter((item) => item.type in editor.schema.blockSpecs);
  }, [dict, editor.schema.blockSpecs]);

  if (block === undefined || items.length === 0) {
    return null;
  }

  function turnInto(item: BlockTypeSelectItem): void {
    const selected = editor.getSelection()?.blocks;
    const targets =
      selected && selected.some((row) => row.id === block!.id) ? selected : [block!];

    editor.focus();
    editor.transact(() => {
      for (const row of targets) {
        editor.updateBlock(row, {
          type: item.type,
          props: item.props,
        } as PartialBlock);
      }
    });
  }

  return (
    <Components.Generic.Menu.Root position="right" sub>
      <Components.Generic.Menu.Trigger sub>
        <Components.Generic.Menu.Item className="bn-menu-item" subTrigger>
          {children}
        </Components.Generic.Menu.Item>
      </Components.Generic.Menu.Trigger>
      <Components.Generic.Menu.Dropdown sub className="bn-menu-dropdown">
        {items.map((item) => {
          const Icon = item.icon;
          const key = `${item.type}-${JSON.stringify(item.props ?? {})}`;
          return (
            <Components.Generic.Menu.Item
              key={key}
              className="bn-menu-item"
              icon={<Icon size={16} />}
              onClick={() => turnInto(item)}
            >
              {item.name}
            </Components.Generic.Menu.Item>
          );
        })}
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}
